import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DataTable, Select, useSelect } from '@chilecompra/react-kit/components'
import routes from '../../config/settings/routes'
import { formatAmount } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import generateYearsBetween from '../../modules/utils/years'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import { SORT_DIRECTIONS_DICTIONARY } from '@chilecompra/react-kit/components/Tables/DataTable.utils'
import { onGetProvidersByFrameworkThunk, onGetFrameworkThunk } from './ProvidersByFrameworkSection.actions'
import { DEFAULT_PROVIDERS_FRAMEWORK_INDICATOR } from '../../config/settings/constants'
import {
  Description,
  GridContainer,
  GridItem,
  Info,
  Link,
  Podium,
  Ranking,
  Root,
  Title
} from './ProvidersByFrameworkSection.styles'

/**
 * The ProvidersByFrameworkSection's container.
 */
const ProvidersByFrameworkSection = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { endYear, latestUpdate, loading, startYear } = useSelector(state => state.topNavbar)
  const [localFrameworkOptions, setLocalFrameworkOptions] = useState([])

  const { csv, data, frameworks } = useSelector(state => state.providersByFrameworkSection)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    overwriteCallback: () => endYear
  })
  const { value: frameworkValue, onChange: handleChangeFramework } = useSelect({
    initialValue: DEFAULT_PROVIDERS_FRAMEWORK_INDICATOR.framework,
    overwriteCallback: () => frameworkValue
  })
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const handleClickRedirect = dni => () => navigate(`${routes.provider.replace(/:dni/, dni)}`)
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    captureImageCallback: () =>
      exportToImage('providers-horizontal-bar-chart', `Info_Principales_Convenio_${yearValue}`),
    downloadFileCallback: () => {
      exportToCsv(csv, `Info_Principales_Proveedores_${yearValue}`)
    }
  })
  useEffect(() => {
    if (!yearValue) {
      return
    }
    dispatch(onGetFrameworkThunk({ year: yearValue }))
  }, [yearValue])

  useEffect(() => {
    if (frameworks.length > 0) {
      setLocalFrameworkOptions(frameworks)
    }
  }, [frameworks, yearValue])

  useEffect(() => {
    if (!yearValue || loading) {
      return
    }
    dispatch(onGetProvidersByFrameworkThunk({ frameworkId: frameworkValue, year: yearValue }))
  }, [yearValue, frameworkValue, loading])

  useEffect(() => {
    if (localFrameworkOptions.length > 0) {
      handleChangeFramework(localFrameworkOptions[0].id)
    }
  }, [localFrameworkOptions])
  return (
    <Root>
      <GridContainer padding="0 0 23px">
        <GridItem xs={12}>
          <Title>Proveedores que más transan por Convenio Marco</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 28px">
        <GridItem xs={12}>
          <Description>
            Revisa el listado de los proveedores con los mayores montos transados con el Estado a través de la Tienda
            Convenio Marco según el año seleccionado.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <DataLayout
            disableCaptureImage={disableCaptureImage}
            disableDownloadFile={disableDownloadFile}
            disableHeader
            initialDisplayedView={DataLayoutViews.TABLE}
            noDataText="No hay datos de organismos proveedores para los filtros seleccionados."
            ref={dataLayoutRef}
            updatedDate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
          >
            <DataLayoutChild view={DataLayoutViews.TABLE}>
              <GridContainer padding="0 0 24px">
                <GridItem xs={12}>
                  <DataTable
                    header={
                      <GridContainer alignItems="center" spacing={1}>
                        <GridItem md={2} xs={12}>
                          <Select
                            label="AÑO"
                            loading={loading}
                            options={yearsOptions}
                            value={yearValue}
                            onChange={handleChangeYear}
                          />
                        </GridItem>
                        <GridItem md={4} xs={12}>
                          <Select
                            label="CONVENIO MARCO"
                            loading={loading}
                            options={localFrameworkOptions}
                            value={frameworkValue}
                            onChange={handleChangeFramework}
                          />
                        </GridItem>
                        <GridItem md={6} xs={12} />
                      </GridContainer>
                    }
                    headers={[
                      { label: 'Ranking', value: 'ranking' },
                      { label: 'Nombre proveedor', value: 'name' },
                      { label: 'RUT proveedor', value: 'dni' },
                      { label: 'N° de órdenes de compra', value: 'quantityOC' },
                      { label: 'Monto transado', value: 'amountCLP' },
                      { label: 'Monto transado ($USD)', value: 'amountUSD' }
                    ]}
                    headersCellsProps={[
                      { enableSort: true, initialDirection: SORT_DIRECTIONS_DICTIONARY.DESC, width: '10%' },
                      { enableSort: true, width: '15%' },
                      { enableSort: true, width: '15%' },
                      { enableSort: true, width: '15%' },
                      { enableSort: true, width: '15%' },
                      { enableSort: true, width: '15%' }
                    ]}
                    loading={loading}
                    records={data}
                    recordsDecorators={[
                      ({ ranking }) =>
                        [1, 2, 3].indexOf(ranking) !== -1 ? (
                          <Podium>{ranking}</Podium>
                        ) : (
                          <Ranking>{ranking || ''}</Ranking>
                        ),
                      ({ dni, name }) => (
                        <Link style={{ textDecoration: 'underline' }} onClick={handleClickRedirect(dni)}>
                          {name}
                        </Link>
                      ),
                      ({ dni }) => dni,
                      ({ quantityOC }) => quantityOC,
                      ({ amountCLP }) => `$${formatAmount(amountCLP)}`,
                      ({ amountUSD }) => `$${formatAmount(amountUSD)}`
                    ]}
                  />
                </GridItem>
              </GridContainer>
              <GridContainer margin="6px 0 0">
                <GridItem xs={12}>
                  <Info>
                    La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                    impuestos)
                  </Info>
                </GridItem>
              </GridContainer>
            </DataLayoutChild>
          </DataLayout>
        </GridItem>
      </GridContainer>
    </Root>
  )
}

export default ProvidersByFrameworkSection
